import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Capability, Role } from '@cannery/shared';
import { api, onUnauthenticated, setAccessToken } from '../lib/api';

export interface AuthUser {
  id: string;
  username: string;
  name: string;
  role: Role;
  branchId: string | null;
  branchCode: string | null;
  branchName: string | null;
}

export interface BranchSummary {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

export type AuthStatus = 'loading' | 'anon' | 'authed';

interface MeResponse {
  user: AuthUser;
  capabilities: Capability[];
  homePath: string;
}

interface LoginResponse {
  accessToken: string;
  user: { id: string };
}

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  capabilities: Capability[];
  homePath: string;
  branches: BranchSummary[];
  /** Admin VIEW filter only (ALL | LH1 | LH2) - never a permission restriction. */
  branchFilter: string;
  setBranchFilter: (branch: string) => void;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Admin VIEW filter only (ALL | branch code) - never a permission restriction.
 *
 * The legacy key `clh.branchFilter` was global per BROWSER, so an admin's
 * selection survived logout and leaked into the next user's session (an LH2
 * employee would then request branch=LH1 and the server correctly answered
 * 403). The filter is now stored per authenticated user id, and every
 * identity change re-establishes the branch context (see applyMe/clearSession).
 */
const BRANCH_FILTER_KEY = 'clh.branchFilter';

const branchFilterKey = (userId: string) => `${BRANCH_FILTER_KEY}.${userId}`;

/** Effective filter when a session is established: admins restore their own
 *  preference; everyone else is ALL (their queue is scoped server-side). */
function readBranchFilter(user: Pick<AuthUser, 'id' | 'role'> | null): string {
  if (user?.role !== 'ADMIN') return 'ALL';
  try {
    return localStorage.getItem(branchFilterKey(user.id)) ?? 'ALL';
  } catch {
    // storage unavailable: default to ALL
    return 'ALL';
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [homePath, setHomePath] = useState('/admin');
  const [branches, setBranches] = useState<BranchSummary[]>([]);
  // Identity is unknown at mount, so the filter starts neutral (ALL) and is
  // resolved for the authenticated user in applyMe below.
  const [branchFilter, setBranchFilterState] = useState<string>('ALL');

  // Purge the legacy identity-less key: its value could belong to any user
  // who used this browser, so it must never be read again.
  useEffect(() => {
    try {
      localStorage.removeItem(BRANCH_FILTER_KEY);
    } catch {
      // storage unavailable: nothing to purge
    }
  }, []);

  const applyMe = useCallback((me: MeResponse) => {
    setUser(me.user);
    setCapabilities(me.capabilities);
    setHomePath(me.homePath);
    // Identity established (login or session restore): set THIS user's branch
    // context. Non-admins always get ALL, so they never inherit a filter.
    setBranchFilterState(readBranchFilter(me.user));
    setStatus('authed');
  }, []);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setCapabilities([]);
    // Logging out drops the branch context too; the stored per-user value
    // (admins only) is untouched so the same user gets it back on re-login.
    setBranchFilterState('ALL');
    setStatus('anon');
  }, []);

  // Session restore on page load: silent refresh via httpOnly cookie.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const me = await api.get<MeResponse>('/auth/me');
        if (!cancelled) applyMe(me);
      } catch {
        if (!cancelled) {
          setAccessToken(null);
          setStatus('anon');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyMe]);

  // Server says the session is dead -> back to login everywhere.
  useEffect(() => onUnauthenticated(() => clearSession()), [clearSession]);

  // Branch list for the admin switcher.
  useEffect(() => {
    if (status !== 'authed' || user?.role !== 'ADMIN') return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.get<{ branches: BranchSummary[] }>('/branches');
        if (!cancelled) setBranches(res.branches.filter((b) => b.isActive));
      } catch {
        // Non-fatal: switcher falls back to ALL.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status, user?.role]);

  const setBranchFilter = useCallback(
    (branch: string) => {
      // Only admins hold a view filter; employees always use their own branch.
      if (user?.role !== 'ADMIN') return;
      setBranchFilterState(branch);
      try {
        localStorage.setItem(branchFilterKey(user.id), branch);
      } catch {
        // storage unavailable: filter still works for this session
      }
    },
    [user],
  );

  const login = useCallback(
    async (username: string, password: string) => {
      const res = await api.postFresh<LoginResponse>('/auth/login', { username, password });
      setAccessToken(res.accessToken);
      const me = await api.get<MeResponse>('/auth/me');
      applyMe(me);
    },
    [applyMe],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Even if the call fails, drop the local session.
    }
    clearSession();
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      capabilities,
      homePath,
      branches,
      branchFilter,
      setBranchFilter,
      login,
      logout,
    }),
    [status, user, capabilities, homePath, branches, branchFilter, setBranchFilter, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
